import axios from 'axios'
import { setupRequestInterceptor } from './request'
import { setupResponseInterceptor } from './response'
import { getCapacitorAdapter, shouldUseNativeAdapter } from '../httpAdapter'
import { API_CONFIG } from '../config'
import { isNativeApp } from '../../device'
import { networkService } from '../../network'
import { csrfManager } from '../csrfManager'

/**
 * Configuração base do axios
 */
function getBaseURL() {
  const envURL = import.meta.env.VITE_API_BASE_URL
  if (envURL) {
    if (envURL.endsWith('/api')) {
      return envURL
    }
    return envURL.endsWith('/') ? `${envURL}api` : `${envURL}/api`
  }
  return 'https://bloopbrasil.com.br/api'
}

/**
 * Inicializa os serviços de API (rede e CSRF)
 * Deve ser chamado na inicialização do app
 */
export async function initApiServices() {
  // Inicializar monitoramento de rede
  await networkService.initialize()

  // Preload do CSRF token
  await csrfManager.preload()

  if (import.meta.env.DEV) {
    console.log('[API] Services initialized', {
      platform: isNativeApp() ? 'native' : 'web',
      adapter: shouldUseNativeAdapter() ? 'CapacitorHttp' : 'XMLHttpRequest',
      network: networkService.getState(),
    })
  }
}

/**
 * Configura os interceptors e retorna a instância do axios
 */
export const setupInterceptors = (axiosInstance = axios) => {
  setupRequestInterceptor(axiosInstance)
  setupResponseInterceptor(axiosInstance)

  return axiosInstance
}

// Middleware para logging
export const setupLoggingMiddleware = () => {
  axios.interceptors.request.use(
    (config) => {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        headers: config.headers,
        data: config.data,
      })
      return config
    },
    (error) => {
      console.error('[API Request Error]', error)
      return Promise.reject(error)
    }
  )

  axios.interceptors.response.use(
    (response) => {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data,
      })
      return response
    },
    (error) => {
      console.error(`[API Response Error] ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
        status: error.response?.status,
        data: error.response?.data,
      })
      return Promise.reject(error)
    }
  )
}

// Middleware para debugging
export const setupDebugMiddleware = () => {
  if (import.meta.env.DEV) {
    setupLoggingMiddleware()
  }
}

/**
 * Cria uma nova instância do axios com todas as otimizações
 */
export function createOptimizedApiClient() {
  const isNative = isNativeApp()

  // Configuração base com timeout dinâmico
  const config = {
    baseURL: getBaseURL(),
    timeout: API_CONFIG.DEFAULT_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    withCredentials: true,
  }

  // Usar adapter nativo se disponível
  if (isNative) {
    config.timeout = API_CONFIG.NATIVE_TIMEOUT
    ;(config as any).adapter = getCapacitorAdapter()
  }

  const instance = axios.create(config)

  // Configurar interceptors
  setupInterceptors(instance)

  return instance
}

// Configuração global
export const apiClient = setupInterceptors(
  axios.create({
    baseURL: getBaseURL(),
    timeout: isNativeApp() ? API_CONFIG.NATIVE_TIMEOUT : API_CONFIG.DEFAULT_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    withCredentials: true,
    // Usar adapter nativo se disponível
    ...(isNativeApp() ? { adapter: getCapacitorAdapter() } : {}),
  })
)

// Exportar configurações e utilitários
export { API_CONFIG }
export { csrfManager } from '../csrfManager'
export { networkService } from '../../network'
